"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, Mail, Check } from "lucide-react";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input/input";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import FilePickerDialog from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import ThumbnailDisplay from "@/modules/hub/features/FileManagement/components/ThumbnailDisplay/ThumbnailDisplay";
import { useEditCampaign } from "@/modules/stochastic/features/CampaignManagement/hooks/useEditCampaign";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import { fetchCampaignOwnerEmployeesLookup } from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignOwnerEmployeesLookup/fetchCampaignOwnerEmployeesLookupApi";
import {
  CustomerRestrictionCriterion,
  DemographicTarget,
  Location,
  Tag,
} from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignDetails/types";

const READ_ONLY_INPUT_CLASSES =
  "bg-gray-50 text-muted-foreground dark:bg-slate-800 dark:border-slate-700 dark:text-gray-400 cursor-not-allowed";

/** Mirrors the shadcn Input shell so chip-based read-only fields match the disabled inputs. */
const READ_ONLY_CONTAINER_CLASSES =
  "flex min-h-9 w-full items-center rounded-md border border-input bg-gray-50 px-3 py-1 text-sm shadow-sm cursor-not-allowed dark:bg-slate-800 dark:border-slate-700";

const CREATIVE_ASSET_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
];
const CREATIVE_ASSET_MAX_FILE_SIZE = 25 * 1024 * 1024;

function EditCampaignPage() {
  const {
    campaignDetails,
    loading,
    error,
    isSaving,
    formData,
    nameError,
    phoneNumberError,
    handleInputChange,
    handleOwnerEmployeeChange,
    handleSubmit,
    handleCancel,
    handleBack,
    creativeAssetFront,
    creativeAssetBack,
    isFrontPickerOpen,
    isBackPickerOpen,
    openFrontPicker,
    closeFrontPicker,
    openBackPicker,
    closeBackPicker,
    handleFrontSelected,
    handleBackSelected,
    handleRemoveFront,
    handleRemoveBack,
  } = useEditCampaign();

  const getDemographicTargetValue = (
    targets: DemographicTarget[] | undefined,
    name: string,
  ): string => targets?.find((target) => target.name === name)?.value ?? "";

  const hasRestrictionCriterion = (
    criteria: CustomerRestrictionCriterion[] | undefined,
    name: string,
    value: string,
  ): boolean =>
    criteria?.some(
      (criterion) => criterion.name === name && criterion.value === value,
    ) ?? false;

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <MainPageWrapper error={error} loading={loading} title="Edit Campaign">
      {campaignDetails && (
        <form className="space-y-8" onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <Button
                className="w-fit mb-4 px-3 py-2 h-auto text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700"
                onClick={handleBack}
                type="button"
                variant="ghost"
              >
                <ArrowLeft className="mr-0.5 h-5 w-10 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-500 dark:text-gray-400">Back</span>
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Product</Label>
                    <Input
                      className={READ_ONLY_INPUT_CLASSES}
                      readOnly
                      value={
                        campaignDetails.campaignProduct.name ||
                        "No product selected"
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Billing Type</Label>
                    <Input
                      className={READ_ONLY_INPUT_CLASSES}
                      readOnly
                      value={
                        campaignDetails.billingType
                          ? campaignDetails.billingType
                              .charAt(0)
                              .toUpperCase() +
                            campaignDetails.billingType.slice(1)
                          : "Not specified"
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="campaignName">Campaign Name</Label>
                    <Input
                      id="campaignName"
                      onChange={(event) =>
                        handleInputChange("name", event.target.value)
                      }
                      placeholder="Enter campaign name"
                      value={formData.name}
                    />
                    {nameError && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {nameError}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Phone Number</Label>
                    <PhoneInput
                      className="w-full border rounded px-2 py-2 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                      country="US"
                      id="phoneNumber"
                      onChange={(value) =>
                        handleInputChange("phoneNumber", value ?? "")
                      }
                      placeholder="Enter phone number"
                      value={formData.phoneNumber}
                    />
                    {phoneNumberError && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {phoneNumberError}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Campaign Owner</Label>
                  <EntitySingleSelect
                    allowEmpty
                    entityNamePlural="Employees"
                    entityNameSingular="Employee"
                    fetchEntities={async ({ searchTerm, page, pageSize }) => {
                      const response = await fetchCampaignOwnerEmployeesLookup({
                        searchTerm,
                        page,
                        pageSize,
                      });

                      return {
                        data: response.data,
                        totalCount:
                          response.meta?.totalCount ?? response.data.length,
                      };
                    }}
                    onChange={handleOwnerEmployeeChange}
                    renderEntityRow={(ent, isSelected, toggle) => (
                      <div
                        className={`cursor-pointer flex items-center justify-between px-3 py-2 text-[13px] border-b last:border-0 text-gray-900 dark:text-white ${
                          isSelected ? "bg-gray-100 dark:bg-gray-800" : ""
                        }`}
                        key={ent.id}
                        onClick={() => toggle(ent)}
                      >
                        <span>{ent.name}</span>
                        {isSelected && (
                          <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                        )}
                      </div>
                    )}
                    value={formData.ownerEmployee}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    onChange={(event) =>
                      handleInputChange("description", event.target.value)
                    }
                    placeholder="Short campaign description"
                    value={formData.description}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      className={READ_ONLY_INPUT_CLASSES}
                      readOnly
                      value={formatDate(campaignDetails.startDate)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      className={READ_ONLY_INPUT_CLASSES}
                      readOnly
                      value={formatDate(campaignDetails.endDate)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">License Number</Label>
                    <Input
                      id="licenseNumber"
                      onChange={(event) =>
                        handleInputChange("licenseNumber", event.target.value)
                      }
                      placeholder="Enter license number"
                      value={formData.licenseNumber}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="permitNumber">Permit Number</Label>
                    <Input
                      id="permitNumber"
                      onChange={(event) =>
                        handleInputChange("permitNumber", event.target.value)
                      }
                      placeholder="Enter permit number"
                      value={formData.permitNumber}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Locations</Label>
                  <div className={READ_ONLY_CONTAINER_CLASSES}>
                    {campaignDetails.locations.length > 0 ? (
                      <div className="flex flex-wrap gap-2 py-1">
                        {campaignDetails.locations.map((location: Location) => (
                          <span
                            className="bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full"
                            key={location.id}
                          >
                            {location.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">
                        No locations selected
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Creative Assets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="[&>div]:max-w-none">
                      <Label className="block mb-2 text-gray-900 dark:text-white">
                        Front
                      </Label>
                      <ThumbnailDisplay
                        acceptedFormatsHint="PDF, PNG, JPG · up to 25MB"
                        onRemove={handleRemoveFront}
                        onSelect={openFrontPicker}
                        thumbnailFileName={creativeAssetFront.fileName}
                        thumbnailUrl={creativeAssetFront.displayUrl}
                      />
                    </div>
                    <div className="[&>div]:max-w-none">
                      <Label className="block mb-2 text-gray-900 dark:text-white">
                        Back
                      </Label>
                      <ThumbnailDisplay
                        acceptedFormatsHint="PDF, PNG, JPG · up to 25MB"
                        onRemove={handleRemoveBack}
                        onSelect={openBackPicker}
                        thumbnailFileName={creativeAssetBack.fileName}
                        thumbnailUrl={creativeAssetBack.displayUrl}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Mailing Schedule</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex items-center gap-2">
                      <Input
                        className={`w-auto ${READ_ONLY_INPUT_CLASSES}`}
                        readOnly
                        value={
                          campaignDetails.mailingSchedule.mailingFrequency
                            .label || "Not specified"
                        }
                      />
                    </div>

                    {campaignDetails.mailingSchedule.mailingDropWeeks.length >
                      0 && (
                      <div className="space-y-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Selected Mailing Weeks
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                          {Array.from(
                            {
                              length:
                                campaignDetails.mailingSchedule.mailingFrequency
                                  .value || 0,
                            },
                            (_, index) => {
                              const weekNumber = index + 1;
                              const isSelected =
                                !!campaignDetails.mailingSchedule.mailingDropWeeks.find(
                                  (week) => week.weekNumber === weekNumber,
                                );

                              return (
                                <div
                                  className={`p-4 rounded-lg border cursor-not-allowed select-none ${
                                    isSelected
                                      ? "bg-gray-100 border-gray-300 dark:bg-slate-700 dark:border-slate-600"
                                      : "bg-gray-50 border-gray-200 dark:bg-slate-800 dark:border-slate-700"
                                  }`}
                                  key={weekNumber}
                                >
                                  <div className="flex flex-col items-center space-y-2">
                                    <Mail
                                      className={`w-6 h-6 ${
                                        isSelected
                                          ? "text-gray-500 dark:text-gray-400"
                                          : "text-gray-300 dark:text-gray-600"
                                      }`}
                                    />
                                    <div
                                      className={`text-sm font-medium ${
                                        isSelected
                                          ? "text-muted-foreground dark:text-gray-400"
                                          : "text-gray-400 dark:text-gray-500"
                                      }`}
                                    >
                                      Week {weekNumber}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    )}

                    <div className="text-sm text-muted-foreground dark:text-gray-400">
                      <span className="font-medium">Total Prospects:</span>{" "}
                      {campaignDetails.totalProspects.toLocaleString()}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Campaign Target
                </h3>
                <RadioGroup
                  className="space-y-2"
                  value={campaignDetails.filters.campaignTarget.value}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled
                      id="include_active_customers_only"
                      value="include_active_customers_only"
                    />
                    <Label htmlFor="include_active_customers_only">
                      Active customers only
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled
                      id="include_prospects_only"
                      value="include_prospects_only"
                    />
                    <Label htmlFor="include_prospects_only">
                      Prospects only
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled
                      id="include_prospects_and_customers"
                      value="include_prospects_and_customers"
                    />
                    <Label htmlFor="include_prospects_and_customers">
                      Both customers and prospects
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Address Type
                </h3>
                <RadioGroup
                  className="space-y-2"
                  value={campaignDetails.filters.addressType.value}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled
                      id="include_residential_only"
                      value="include_residential_only"
                    />
                    <Label htmlFor="include_residential_only">
                      Residential
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled
                      id="include_commercial_only"
                      value="include_commercial_only"
                    />
                    <Label htmlFor="include_commercial_only">Commercial</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled
                      id="include_both_residential_and_commercial"
                      value="include_both_residential_and_commercial"
                    />
                    <Label htmlFor="include_both_residential_and_commercial">
                      Both Residential &amp; Commercial
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {campaignDetails.showTagSelector &&
                campaignDetails.filters.tags.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Selected Tags
                    </h3>
                    <div className={READ_ONLY_CONTAINER_CLASSES}>
                      <div className="flex flex-wrap gap-2 py-1">
                        {campaignDetails.filters.tags.map(
                          (tag: Tag, index: number) => (
                            <span
                              className="bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full"
                              key={index}
                            >
                              {tag.name}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {campaignDetails.showDemographicTargets && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Demographic Targets
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Prospect Age Range</Label>
                      <div className="flex gap-2">
                        <Input
                          className={READ_ONLY_INPUT_CLASSES}
                          placeholder="Min"
                          readOnly
                          value={getDemographicTargetValue(
                            campaignDetails.filters.demographicTargets,
                            "prospect_min_age",
                          )}
                        />
                        <Input
                          className={READ_ONLY_INPUT_CLASSES}
                          placeholder="Max"
                          readOnly
                          value={getDemographicTargetValue(
                            campaignDetails.filters.demographicTargets,
                            "prospect_max_age",
                          )}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Minimum Estimated Income</Label>
                      <Input
                        className={READ_ONLY_INPUT_CLASSES}
                        readOnly
                        value={
                          getDemographicTargetValue(
                            campaignDetails.filters.demographicTargets,
                            "min_estimated_income",
                          ) || "Not specified"
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Minimum Home Age</Label>
                      <Input
                        className={READ_ONLY_INPUT_CLASSES}
                        readOnly
                        value={
                          getDemographicTargetValue(
                            campaignDetails.filters.demographicTargets,
                            "home_min_age",
                          ) || "Not specified"
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {campaignDetails.showCustomerRestrictionCriteria && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Existing Customer Restriction Criteria
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={hasRestrictionCriterion(
                          campaignDetails.filters.customerRestrictionCriteria,
                          "customer_max_ltv",
                          "5000",
                        )}
                        disabled
                        id="excludeLTV"
                      />
                      <Label htmlFor="excludeLTV">
                        Exclude LTV greater than $5000
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={hasRestrictionCriterion(
                          campaignDetails.filters.customerRestrictionCriteria,
                          "club_members_inclusion",
                          "exclude_club_members",
                        )}
                        disabled
                        id="excludeClubMembers"
                      />
                      <Label htmlFor="excludeClubMembers">
                        Exclude club members
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={hasRestrictionCriterion(
                          campaignDetails.filters.customerRestrictionCriteria,
                          "customer_installations_inclusion",
                          "exclude_customer_installations",
                        )}
                        disabled
                        id="excludeCustomerInstallations"
                      />
                      <Label htmlFor="excludeCustomerInstallations">
                        Exclude customer installations
                      </Label>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Selected Postal Codes
                </h3>
                <div className="relative w-full overflow-auto rounded-md border dark:border-slate-700">
                  <table className="w-full caption-bottom text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
                        <th className="p-4 text-left align-middle font-medium text-gray-900 dark:text-white">
                          Postal Code
                        </th>
                        <th className="p-4 text-left align-middle font-medium text-gray-900 dark:text-white">
                          Selected Prospects
                        </th>
                      </tr>
                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                      {campaignDetails.postalCodeLimits.map(
                        (postalCodeLimit) => (
                          <tr
                            className="border-b dark:border-slate-700"
                            key={postalCodeLimit.postalCode}
                          >
                            <td className="p-4 align-middle font-medium text-gray-900 dark:text-white">
                              {postalCodeLimit.postalCode}
                            </td>
                            <td className="p-4 align-middle text-gray-900 dark:text-white">
                              {postalCodeLimit.limit.toLocaleString()}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  disabled={isSaving}
                  onClick={handleCancel}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button disabled={isSaving} type="submit">
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <FilePickerDialog
            allowedFileTypes={CREATIVE_ASSET_FILE_TYPES}
            isOpen={isFrontPickerOpen}
            maxFileSize={CREATIVE_ASSET_MAX_FILE_SIZE}
            multiSelect={false}
            namespace="campaign"
            onClose={closeFrontPicker}
            onSelect={handleFrontSelected}
            title="Select Front Creative"
          />
          <FilePickerDialog
            allowedFileTypes={CREATIVE_ASSET_FILE_TYPES}
            isOpen={isBackPickerOpen}
            maxFileSize={CREATIVE_ASSET_MAX_FILE_SIZE}
            multiSelect={false}
            namespace="campaign"
            onClose={closeBackPicker}
            onSelect={handleBackSelected}
            title="Select Back Creative"
          />
        </form>
      )}
    </MainPageWrapper>
  );
}

export default EditCampaignPage;
